import { useNotifications } from '@/data/Content/Notifications';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { searchTermAssociationFetcher } from '@/data/Content/_SearchTermAssociationFetcher';
import { useSettings } from '@/data/Settings';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { REGEX } from '@/data/constants/regex';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { processError } from '@/data/utils/processError';

/**
 * Resolves a term (brand, keyword, etc.) against the searchdisplay/bySearchTerm
 * redirect association before navigating, falling back to `fallbackHref` when
 * no redirect is configured.
 */
export const useSearchTermRedirect = () => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const params = useExtraRequestParameters(false);
	const { storeId } = getClientSideCommon(settings, router);
	const { notifyError } = useNotifications();

	return async (searchTerm: string, fallbackHref: string): Promise<void> => {
		let data;
		try {
			data = await searchTermAssociationFetcher(true)({ storeId, searchTerm, params });
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		}
		const urlParts = data?.redirecturl
			?.replace(REGEX.SEARCH_REDIRECT_SUFFIX, EMPTY_STRING)
			.split('?');

		if (urlParts?.at(0)) {
			const [pathname, hashValue] = (urlParts[0] || '').split('#');
			router.push(
				{
					pathname,
					query: urlParts[1],
					...(hashValue ? { hash: hashValue } : {}),
				},
				undefined,
				{ shallow: false }
			);
		} else {
			router.push({ pathname: fallbackHref }, undefined, { shallow: false });
		}
	};
};
